import type { StaticImageData } from 'next/image';
// eslint-disable-next-line deprecate/import
import Link from 'next/link';

import type { ComponentType, ReactNode } from 'react';

import { uniqueId } from 'lodash';

import { GENERAL_URLS } from '@shared/constants/urls';

import { ReactComponent as Question } from '@launchpad/assets/icons/question.svg';

import TokenProviders from '../Portfolio/ui/Balance/TokenProviders';

export const getUniqueID = () => uniqueId('how_it_works_');

type Pools = {
  poolId: string;
  image: StaticImageData | string;
  poolHeader: string;
  poolDescription: ReactNode;
};

type SubComponent = {
  Icon: ComponentType;
  header: string;
  description: string;
};

type HowItWorks = {
  id: string;
  step: string;
  header: string;
  description?: ReactNode;
  subcomponent?: SubComponent;
  subDescription?: string;
  pools?: Pools[];
  content?: ReactNode;
};

export const processes: HowItWorks[] = [
  {
    id: 'step-1',
    step: 'Step 1',
    header: 'Register on Magic Launchpad',
    description: (
      <>
        Connect an EVM compatible wallet to the platform using the Binance Smart Chain Network by
        clicking on “Connect Wallet” at the top corner.
        <br />
        Select your wallet from the popup menu. We recommend using Metamask for a seamless
        experience.
      </>
    ),
    subcomponent: {
      Icon: Question,
      header: 'Note:',
      description: 'Support for additional EVM Networks and chains is planned for the future.',
    },
  },
  {
    id: 'step-2',
    step: 'Step 2',
    header: 'Complete KYC Verification',
    description: (
      <>
        KYC verification is required to participate in token sales.
        <br />
        After wallet connection, click “Get Verified” on the top right or find the KYC option in
        your Dashboard.
        <br />
        Post-verification, you will be eligible to join token sales. If you experience issues with
        your KYC Verification, please contact our support team.
      </>
    ),
  },
  {
    id: 'step-3',
    step: 'Step 3',
    header: 'Understand IDO Types and Pools',
    description:
      'Initially, all sales are first-come, first-served (FCFS). Sales close once the Raising Goal is reached.',
    subDescription: 'Types of pools',
    pools: [
      {
        poolId: getUniqueID(),
        image: 'launchpad/users-in-waves.png',
        poolHeader: 'Open to All',
        poolDescription: 'No whitelist required.',
      },
      {
        poolId: getUniqueID(),
        image: 'launchpad/magic-square-logo-in-waves-secondary-color.png',
        poolHeader: 'SQRp Required',
        poolDescription: (
          <>
            Participation requires depositing SQRp Points in addition to USDT, which you can get by{' '}
            <Link
              className="text-primary-600 underline"
              href={GENERAL_URLS.staking}
              target="_blank"
            >
              Staking SQR
            </Link>{' '}
            tokens or holding a{' '}
            <Link
              className="text-primary-600 underline"
              href="https://docs.magicsquare.io/for-users/magic-memberships"
              target="_blank"
            >
              Magic Store Membership.
            </Link>
          </>
        ),
      },
      {
        poolId: getUniqueID(),
        image: 'launchpad/crown-in-waves.png',
        poolHeader: 'Whitelisted Users Only',
        poolDescription:
          'Specific prerequisites must be met for access, you can find the full details on each project page.',
      },
    ],
  },
  {
    id: 'step-4',
    step: 'Step 4',
    header: 'Select a Project and Pool',
    description: (
      <>
        {`Review the project's page to understand the available pools and requirements.`}
        <br />
        {`Make sure all prerequisites are completed before the sale starts, as late participation is
        not allowed.`}
      </>
    ),
  },
  {
    id: 'step-5',
    step: 'Step 5',
    header: 'Prepare Your Wallet and SQRp Balance',
    content: (
      <>
        <p>
          Ensure your wallet contains the amount you wish to deposit in USDT (BEP20) and BNB to
          cover transaction fees.
          <br />
          If participating in a SQRp-required pool, ensure you stake enough SQRp equivalent to your
          USDT deposit amount (1 USDT = 1 SQRp).
        </p>

        <TokenProviders className="mt-6" />
      </>
    ),
  },
  {
    id: 'step-6',
    step: 'Step 6',
    header: 'Participate in the Sale',
    description: (
      <>
        {`Note the token sale's starting date and make sure to have a stable internet connection.`}
        <br />
        Visit the project deposit page at the start of the sale to deposit USDT (and SQRp if
        required).
      </>
    ),
  },
  {
    id: 'step-7',
    step: 'Step 7',
    header: 'Claim Your Tokens',
    description: (
      <>
        {`After the sale, a snapshot determines the eligible investors.`}
        <br />
        {`Wait for the Token Generation Event (TGE), after which tokens are distributed based on the
        vesting conditions outlined on each project page.`}
        <br />
        {`Tokens can be claimed via the Magic Launchpad Dashboard under "My Participations" or
        directly through the project, depending on the project's distribution method.`}
        <br />
        {`Additional details are project-specific and will be provided accordingly.`}
      </>
    ),
  },
];
